import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import ModalUsados from "../../components/Modal";
import ContactFloating from "../../components/contact-floating";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './Usados.styles.scss';

const Usados = () => {
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [priceFilter, setPriceFilter] = useState('all');
    const [brandFilter, setBrandFilter] = useState('all');

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
        fetchVehicles();
    }, []);

    const fetchVehicles = async () => {
        try {
            setLoading(true);
            const res = await fetch('https://dindorf.vercel.app/api/vehicles', {
                cache: 'no-store',
            });
            if (!res.ok) throw new Error('Error al obtener los vehículos');
            const data = await res.json();
            
            // Filtrar solo vehículos usados (que tengan año y kilometraje > 0)
            const usedVehicles = data.filter(
                ({ año, kilometraje }) => año !== 0 && kilometraje !== 0
            );
            
            setVehicles(usedVehicles);
        } catch (error) {
            console.error('Error cargando los vehículos:', error);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (vehicle) => {
        if (vehicle && Object.keys(vehicle).length > 0) {
            setSelectedVehicle(vehicle);
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedVehicle(null);
    };

    // Get unique brands for filter
    const uniqueBrands = [...new Set(vehicles.map(vehicle => vehicle.marca))].sort();

    // Filter vehicles based on search and filters
    const filteredVehicles = vehicles.filter(vehicle => {
        const matchesSearch = 
            vehicle.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.variante?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesBrand = brandFilter === 'all' || vehicle.marca === brandFilter;
        
        const matchesPrice = priceFilter === 'all' || (() => {
            const price = vehicle.precioVenta;
            switch(priceFilter) {
                case 'under10': return price < 10000000;
                case '10to20': return price >= 10000000 && price < 20000000;
                case '20to30': return price >= 20000000 && price < 30000000;
                case 'over30': return price >= 30000000;
                default: return true;
            }
        })();

        return matchesSearch && matchesBrand && matchesPrice;
    });

    return (
        <div className="usados-page">
            <section className="usados-hero section animate__animated animate__fadeIn">
                <div className="container usados-container">
                    <h1 className="section-title">Vehículos Usados</h1>
                    
                    <div className="page-intro">
                        <p className="intro-text">
                            Descubre nuestra selección de vehículos usados de calidad. Todos nuestros autos 
                            pasan por una rigurosa inspección técnica para garantizar su buen estado y confiabilidad.
                        </p>
                    </div>

                    {/* Filtros */}
                    <div className="filters-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Buscar por marca, modelo o versión..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            <i className="ri-search-line search-icon"></i>
                        </div>

                        <div className="filter-group">
                            <select
                                value={brandFilter}
                                onChange={(e) => setBrandFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">Todas las marcas</option>
                                {uniqueBrands.map(brand => (
                                    <option key={brand} value={brand}>{brand}</option>
                                ))}
                            </select>

                            <select
                                value={priceFilter}
                                onChange={(e) => setPriceFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">Todos los precios</option>
                                <option value="under10">Hasta $10.000.000</option>
                                <option value="10to20">$10.000.000 - $20.000.000</option>
                                <option value="20to30">$20.000.000 - $30.000.000</option>
                                <option value="over30">Más de $30.000.000</option>
                            </select>
                        </div>
                    </div>

                    {/* Resultados */}
                    <div className="results-info">
                        <span className="results-count">
                            {filteredVehicles.length} vehículo{filteredVehicles.length !== 1 ? 's' : ''} encontrado{filteredVehicles.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {/* Grid de vehículos */}
                    <div className="vehicles-content">
                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Cargando vehículos...</p>
                            </div>
                        ) : filteredVehicles.length > 0 ? (
                            <div className="vehicles-grid">
                                {filteredVehicles.map((vehicle) => (
                                    <div key={vehicle._id} className="vehicle-card">
                                        <div className="vehicle-image">
                                            <img
                                                src={vehicle.fotos?.[0] || 'https://via.placeholder.com/300'}
                                                alt={`${vehicle.marca} ${vehicle.modelo}`}
                                                loading="lazy"
                                            />
                                            <div className="vehicle-year-badge">
                                                {vehicle.año}
                                            </div>
                                        </div>

                                        <div className="vehicle-info">
                                            <h3 className="vehicle-title">
                                                {vehicle.marca} {vehicle.modelo}
                                            </h3>
                                            <p className="vehicle-variant">
                                                {vehicle.variante}
                                            </p>
                                            
                                            <div className="vehicle-details">
                                                <div className="detail-item">
                                                    <i className="ri-dashboard-line"></i>
                                                    <span>
                                                        {vehicle.kilometraje 
                                                            ? vehicle.kilometraje.toLocaleString('es-ES') + ' km'
                                                            : 'No disponible'
                                                        }
                                                    </span>
                                                </div>
                                                <div className="detail-item">
                                                    <i className="ri-gas-station-line"></i>
                                                    <span>{vehicle.tipoCombustible || 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <i className="ri-settings-line"></i>
                                                    <span>{vehicle.transmision || 'N/A'}</span>
                                                </div>
                                            </div>

                                            <div className="vehicle-price">
                                                $
                                                {vehicle.precioVenta
                                                    ? vehicle.precioVenta.toLocaleString('es-ES')
                                                    : 'Consultar'
                                                }
                                            </div>

                                            <button
                                                className="info-button"
                                                onClick={() => openModal(vehicle)}
                                            >
                                                <i className="ri-information-line"></i>
                                                Ver más información
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-results">
                                <i className="ri-car-line no-results-icon"></i>
                                <h3>No se encontraron vehículos</h3>
                                <p>Intenta cambiar los filtros o realizar una nueva búsqueda</p>
                            </div>
                        )}
                    </div>

                    {/* Call to action */}
                    <div className="cta-section">
                        <h2>¿No encontraste lo que buscabas?</h2>
                        <p>Contáctanos y te ayudamos a encontrar el vehículo perfecto para ti</p>
                        <button
                            className="cta-button"
                            onClick={() =>
                                window.open(
                                    'https://wa.me/5492634588256?text=Hola,%20estoy%20interesado%20en%20vehículos%20usados',
                                    '_blank'
                                )
                            }
                        >
                            <i className="ri-whatsapp-line"></i>
                            Contactar Ventas
                        </button>
                    </div>
                </div>
            </section>

            <ModalUsados
                isOpen={isModalOpen}
                onClose={closeModal}
                selectedItem={selectedVehicle}
            />

            <ContactFloating />
        </div>
    );
};

export default Usados;